/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import _ from 'lodash';

export const allowFileUpload = {
    'image/jpeg': [],
    'image/png': [],
};

export const allowPDFUpload = { 'application/pdf': [] };

export const allowVideoUpload = {
    'video/mp4': [],
    'video/quicktime': [],
};

export type FileUploadType = typeof allowFileUpload | typeof allowVideoUpload;

export interface AcceptedFile extends File {
    preview?: string;
}

export const getAllowFileUpload = (
    enabledVideo: boolean = false,
    hasVideoRequirement: boolean = false,
    enabledPDF: boolean = true,
    requirementTypeId: string | undefined = undefined
): FileUploadType => {
    if (
        (enabledVideo || hasVideoRequirement) &&
        requirementTypeId === 'VIDEO'
    ) {
        return { ...allowVideoUpload };
    }
    let allowFile = allowFileUpload;
    if (enabledPDF) {
        allowFile = { ...allowFile, ...allowPDFUpload };
    }
    if ((enabledVideo || hasVideoRequirement) && !requirementTypeId) {
        allowFile = { ...allowFile, ...allowVideoUpload };
    } else if (enabledVideo) {
        allowFile = { ...allowFile, ...allowVideoUpload };
    }
    return allowFile;
};

export const getFileUploadErrorMessage = (allowFiles: FileUploadType) => {
    const isImage = _.some(allowFiles, (_, mimeType) =>
        isImageMimeType(mimeType)
    );
    const isVideo = _.some(allowFiles, (_, mimeType) =>
        isVideoMimeType(mimeType)
    );
    const isDocument = _.some(allowFiles, (_, mimeType) =>
        isPdfMimeType(mimeType)
    );
    return (
        <>
            The file you attempted to upload was not a supported file types.
            Please re-upload using supported file types which include:
            {isImage && (
                <>
                    <br />
                    Photo: JPG or PNG
                </>
            )}
            {isVideo && (
                <>
                    <br />
                    Video: MP4 or MOV
                </>
            )}
            {isDocument && (
                <>
                    <br />
                    Document: PDF
                </>
            )}
        </>
    );
};

export const isImageMimeType = (mimeType?: string): boolean => {
    return mimeType?.startsWith('image/') ?? false;
};

export const isPdfMimeType = (mimeType?: string): boolean => {
    return mimeType?.startsWith('application/pdf') ?? false;
};

export const isVideoMimeType = (mimeType?: string): boolean => {
    return mimeType?.startsWith('video/') ?? false;
};
